import { render, type JSXChild } from 'engine/index';
import { rpc } from 'src/backend';
import { Table, Td, Th, TrBody, TrHead } from './TableComponents';
import { Button } from './Button';
import { CheckboxField, ToggleField } from './FormComponents';
import type {
	BouwHetLandschapAnswer,
	BouwHetLandschapPitch,
	BouwHetLandschapPlayerState,
	BouwHetLandschapState,
} from 'shared/modelTypes/bouw-het-landschap';
import { Icon } from './Icon';
import { Plus } from 'lucide-static';

function countAnsweredQuestions(answers: Record<string, BouwHetLandschapAnswer>, playerId: string) {
	let count = 0;
	Object.values(answers).forEach((a) => {
		if (typeof a === 'object' && a?.playerId === playerId && a?.answer != '') {
			count++;
		}
	});

	return count;
}

function countPreparedPitches(answers: Record<string, BouwHetLandschapAnswer>, playerId: string) {
	let count = 0;

	Object.values(answers).forEach((a) => {
		if (a.pitch && a.playerId === playerId) {
			count++;
		}
	});

	return count;
}

export function PlayBouwHetLandschap({ licenseCode }: { licenseCode: string }) {
	const view: HTMLElement = <div>Will play game {licenseCode}</div>;

	const sessionId = licenseCode;

	rpc.onAdminUpdates.subscribe(
		{ sessionId },
		{
			onData(data) {
				//
				if (data.kind === 'update-state') {
					const state = data.state as BouwHetLandschapState;
					renderStage(state);
				}
			},
		},
	);

	const renderStage = (state: BouwHetLandschapState) => {
		const s = state.stage;

		switch (s) {
			case 'starting':
				render(view, <div class="animate-pulse text-xl text-gray-700">Starting play session</div>);
				break;
			case 'register-players':
				render(
					view,
					<PlayerRegisterView
						onContinue={() => {
							console.log('start the game');
							rpc.sendBouwHetLandschapMessage.query({ sessionId, update: { kind: 'start-game' } });
						}}
						state={state}
					/>,
				);
				break;
			case 'answer-questions':
				{
					const cols = new Map();
					cols.set(
						'Vragen Beantwoord',
						(p: BouwHetLandschapPlayerState) => `${countAnsweredQuestions(state.answers || {}, p.id)} / 8`,
					);
					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Vragen aan het beantwoorden</h2>
							<Button
								on:click={() => {
									rpc.sendBouwHetLandschapMessage.query({ sessionId, update: { kind: 'end-questions' } });
								}}
							>
								{state.questionRoundIndex < 3
									? `Volgende ronde (${state.questionRoundIndex + 2})`
									: 'Start uitleg presenatieronde'}
							</Button>

							<PlayerTable sessionId={sessionId} state={state} cols={cols} />
						</div>,
					);
				}
				break;
			case 'wait-for-theme-and-area-presenting':
				{
					const cols = new Map();
					cols.set(
						'Vragen Beantwoord',
						(p: BouwHetLandschapPlayerState) => `${countAnsweredQuestions(state.answers || {}, p.id)} / 8`,
					);

					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Vragen beantwoord</h2>
							<p>Leg het spel uit, laat alle teams 8 tegels leggen en begin presentatie ronde.</p>
							<Button
								on:click={() => {
									rpc.sendBouwHetLandschapMessage.query({
										sessionId,
										update: { kind: 'start-theme-and-area-presenting' },
									});
								}}
							>
								Start presentatie mode
							</Button>

							<PlayerTable sessionId={sessionId} state={state} cols={cols} />
						</div>,
					);
				}
				break;
			case 'theme-and-area-presenting':
				{
					const cols = new Map();
					cols.set(
						'Vragen Beantwoord',
						(p: BouwHetLandschapPlayerState) => `${countAnsweredQuestions(state.answers || {}, p.id)} / 8`,
					);

					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Vragen beantwoord</h2>
							<p>Laat alle teams 8 tegels leggen en begin presentatie ronde.</p>
							<Button
								on:click={() => {
									rpc.sendBouwHetLandschapMessage.query({
										sessionId,
										update: { kind: 'start-rounds' },
									});
								}}
							>
								Start presentatie mode
							</Button>

							<PlayerTable sessionId={sessionId} state={state} cols={cols} />
						</div>,
					);
				}
				break;
			case 'wait-for-rounds':
				{
					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Vragen beantwoord</h2>
							<p>Geen uitleg en druk op de knop om de rondes te beginnen.</p>
							<Button
								on:click={() => {
									rpc.sendBouwHetLandschapMessage.query({ sessionId, update: { kind: 'start-rounds' } });
								}}
							>
								Begin Rondes
							</Button>

							<PlayerTable sessionId={sessionId} state={state} />
						</div>,
					);
				}
				break;
			case 'play-rounds':
				{
					const player = state.players[state.currentPlayerIndex];
					const playerScreen = player.lastScreen.kind;

					const cols = new Map();
					cols.set('Punten toekennen', (p: BouwHetLandschapPlayerState) => {
						return (
							<form
								on:submit={(e: SubmitEvent) => {
									e.preventDefault();
									const form = e.target as HTMLFormElement;
									const data = new FormData(form);
									const points = Number(data.get('points'));

									if (typeof points === 'number') {
										player.score + points;
										rpc.sendBouwHetLandschapMessage.query({
											sessionId,
											update: {
												kind: 'player-finished-street',
												playerId: p.id,
												points,
											},
										});
									}
								}}
								class="flex"
							>
								<input
									type="number"
									name="points"
									placeholder="Punten"
									class="focus:shadow-outline appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none"
								/>
								<button
									type="submit"
									class="focus:shadow-outline ml-2 rounded-full bg-theme-600 p-2 font-bold text-white hover:bg-theme-700 focus:outline-none"
								>
									<Icon icon={Plus} class="size-6 text-white" />
								</button>
							</form>
						);
					});

					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Ronde {state.roundIndex + 1}</h2>
							{playerScreen == 'show-choose-diceroll-screen' && (
								<div>
									<p>{player.name} is aan het dobbelen.</p>
								</div>
							)}
							<div class="size-8"></div>
							<ToggleField
								compact
								name="lastRound"
								label="Laatste ronde"
								value={state.lastRound}
								onChange={(v) => {
									const lastRound = v || false;
									rpc.sendTeamKleurenMessage.query({ sessionId, update: { kind: 'set-is-last-round', lastRound } });
								}}
							/>
							<div class="size-8"></div>
							<PlayerTable sessionId={sessionId} state={state} cols={cols} />
						</div>,
					);
				}
				break;
			case 'prepare-pitches':
				{
					const cols = new Map();
					cols.set(
						'Pitches voorbereid',
						(p: BouwHetLandschapPlayerState) => `${countPreparedPitches(state.answers || {}, p.id)} / 2`,
					);

					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Pitches voorbereiden</h2>
							<p>Laat alle teams 2 pitches voorbereiden en start de pitch battles.</p>
							<Button
								on:click={() => {
									rpc.sendBouwHetLandschapMessage.query({ sessionId, update: { kind: 'end-pitch-prepare' } });
								}}
							>
								Start pitch battles
							</Button>

							<PlayerTable sessionId={sessionId} state={state} cols={cols} />
						</div>,
					);
				}
				break;
			case 'wait-for-pitch-battle':
				{
					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Pitches voorbereid</h2>
							<p>Leg het spel uit en start de pitch battles.</p>
							<Button
								on:click={() => {
									rpc.sendBouwHetLandschapMessage.query({
										sessionId,
										update: { kind: 'start-pitch-battles' },
									});
								}}
							>
								Start pitch battles
							</Button>

							<PlayerTable sessionId={sessionId} state={state} />
						</div>,
					);
				}
				break;
			case 'pitch-battles':
				{
					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Pitch battle ({state.pitchRoundIndex + 1})</h2>
							<p>Twee teams mogen hun pitch presenteren.</p>
							<Button
								on:click={() => {
									rpc.sendBouwHetLandschapMessage.query({ sessionId, update: { kind: 'end-pitch' } });
								}}
							>
								Pitch beëindigen
							</Button>

							<PlayerTable sessionId={sessionId} state={state} />
						</div>,
					);
				}
				break;
			case 'wait-for-pitch':
				{
					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Pitch voorbij</h2>
							<p>Start de volgende pitch.</p>
							<Button
								on:click={() => {
									rpc.sendBouwHetLandschapMessage.query({
										sessionId,
										update: { kind: 'start-pitch' },
									});
								}}
							>
								Start volgende pitch
							</Button>

							<PlayerTable sessionId={sessionId} state={state} />
						</div>,
					);
				}
				break;
			case 'pitch-review':
				{
					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Review pitch battle ({state.pitchRoundIndex + 1})</h2>
							<p>De overige teams mogen de pitches beoordelen.</p>
							<Button
								on:click={() => {
									rpc.sendBouwHetLandschapMessage.query({ sessionId, update: { kind: 'end-pitch-round' } });
								}}
							>
								Pitch ronde beëindigen
							</Button>

							<PlayerTable sessionId={sessionId} state={state} />
						</div>,
					);
				}
				break;

			case 'ending-report':
				{
					render(
						view,
						<div>
							<p>Showing ending report</p>
							<PlayerTable state={state} />
						</div>,
					);
				}
				break;
		}
	};

	// render initial state
	(async () => {
		const state = await rpc.getGameState.query({ sessionId });
		console.log(state);
		renderStage(state);
	})();

	return view;
}

function PlayerRegisterView({ state, onContinue }: { state: BouwHetLandschapState; onContinue: () => void }) {
	console.log('render players', state.players);

	const playersWithName = state.players.filter((p) => p.name);

	return (
		<div>
			<h3 class="text-lg font-bold text-gray-800 dark:text-gray-200">Spelers registreren</h3>
			<p class="mb-5 text-gray-700">
				Laat spelers de QR code op het bord scannen en vervolgens hun gegevens invullen.
				<br />
				Druk op <strong>doorgaan</strong> als alle spelers gereed zijn.
			</p>
			{playersWithName.length > 0 && (
				<Table>
					<thead>
						<TrHead>
							<Th>Name</Th>
							<Th>Color</Th>
							<Th>Id</Th>
						</TrHead>
					</thead>
					<tbody>
						{playersWithName.map((player) => {
							return (
								<TrBody>
									<Td>{player.name}</Td>
									<Td>{player.color}</Td>
									<Td>{player.id}</Td>
								</TrBody>
							);
						})}
					</tbody>
				</Table>
			)}
			{!playersWithName.length && <small class="text-gray-700">Er zijn nog geen spelers.</small>}
			{playersWithName.length > 0 && playersWithName.length < 4 && (
				<small class="text-gray-700">Je moet met minimaal 4 spelers spelen.</small>
			)}
			<div class="mt-5"></div>
			<Button on:click={onContinue} disabled={playersWithName.length < 4}>
				Doorgaan
			</Button>
		</div>
	);
}

function PlayerTable({
	state,
	sessionId,
	cols,
}: {
	state: BouwHetLandschapState;
	sessionId?: string;
	cols?: Map<string, (p: BouwHetLandschapPlayerState) => JSXChild>;
}) {
	return (
		<Table>
			<thead>
				<TrHead>
					{sessionId && <Th title="When disabled the players turn will always be skipped">Active</Th>}
					<Th>Name</Th>
					<Th>Color</Th>
					<Th>Punten</Th>
					{/* <Th>Antwoord</Th> */}
					{cols && [...cols.entries()].map(([k, _]) => <Th>{k}</Th>)}
					<Th>id</Th>
				</TrHead>
			</thead>
			<tbody>
				{state.players.map((player) => {
					return (
						<TrBody>
							{sessionId && (
								<Td>
									<CheckboxField
										name={player.id + '-active'}
										label={''}
										value={player.active}
										onChange={(v) => {
											rpc.sendFlikkenMessage.query({
												sessionId,
												update: { kind: 'set-player-active', playerId: player.id, active: v ?? true },
											});
										}}
									/>
								</Td>
							)}
							<Td>{player.name}</Td>
							<Td>{player.color}</Td>
							<Td>{player.score}</Td>
							{cols && [...cols.entries()].map(([_, v]) => <Td>{v(player)}</Td>)}
							{/* <Td>{player.roundAnswers[state.roundIndex]?.emotion ?? ''}</Td> */}
							<Td>{player.id}</Td>
						</TrBody>
					);
				})}
			</tbody>
		</Table>
	);
}
